import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthState } from '../context/UserAuthentication';
import {
  resolveAutoTrackedPulseViewId,
  trackPulseView,
} from './pulseViewIds';

/**
 * Fires Pulse `view` for mapped routes on pathname change.
 * Menu, Rewards, and Profile tabs are excluded (local handlers).
 * Auth-gated routes (scan, profile devices) skip until the user is logged in.
 */
const PulseViewTracker = (): null => {
  const { pathname } = useLocation();
  const { authInfo } = useAuthState();
  const userId = authInfo?.userId;
  const lastKeyRef = useRef<string | null>(null);
  const isAuthenticated = Boolean(authInfo?.authentication_token);

  useEffect(() => {
    // Wait for guest-customer (or hydrated session) before auto route views.
    if (!userId) return;

    const viewId = resolveAutoTrackedPulseViewId(pathname, isAuthenticated);

    // Routes handled elsewhere (/menu, /rewards, /profileDetails) return null.
    // Clear dedupe so returning to Home (or any tracked route) still fires.
    if (!viewId) {
      lastKeyRef.current = null;
      return;
    }

    const key = `${viewId}|${pathname}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    trackPulseView(viewId, { page_path: pathname });
  }, [pathname, isAuthenticated, userId]);

  return null;
};

export default PulseViewTracker;
